import { Loader2, Pause, Play } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { formatClock } from './PodcastPill';
import { PodcastWave } from './PodcastWave';

/** Speeds the chip steps through: a nudge up is the common case. */
const SPEEDS = [1, 1.25, 1.5, 2, 0.75];

/**
 * The round play / pause control of the in-text player and its dock - the
 * app's action pill shape, so it reads as the one thing to press.
 */
export function PlayPauseButton({
  playing,
  onClick,
  busy = false,
  size = 'md',
  label,
}: {
  playing: boolean;
  onClick: () => void;
  busy?: boolean;
  size?: 'sm' | 'md';
  /** What it plays, for the accessible name: "Play podcast of section 2". */
  label: string;
}) {
  const Icon = busy ? Loader2 : playing ? Pause : Play;
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={busy}
      aria-label={`${playing ? 'Pause' : 'Play'} ${label}`}
      className={cn(
        'tap-target flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-colors duration-fast hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-70',
        size === 'md' ? 'h-11 w-11' : 'h-8 w-8'
      )}
    >
      <Icon
        className={cn(
          size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5',
          !playing && !busy && 'translate-x-px',
          busy && 'animate-spin motion-reduce:animate-none'
        )}
        aria-hidden='true'
      />
    </button>
  );
}

/** A position slider with elapsed / total time under or beside it. */
export function SeekBar({
  currentTime,
  duration,
  onSeek,
  compact = false,
  peaks,
  className,
}: {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  /** One line: slider and `1:12 / 4:05` side by side. */
  compact?: boolean;
  /** The waveform: drawn as bars that seek, instead of a plain slider. */
  peaks?: number[];
  className?: string;
}) {
  const max = Math.max(1, Math.floor(duration));
  const slider = peaks ? (
    <PodcastWave
      peaks={peaks}
      currentTime={currentTime}
      duration={duration}
      onSeek={onSeek}
    />
  ) : (
    <input
      type='range'
      min={0}
      max={max}
      step={1}
      value={Math.min(max, Math.floor(currentTime))}
      onChange={e => onSeek(Number(e.target.value))}
      aria-label='Position'
      aria-valuetext={`${formatClock(currentTime)} of ${formatClock(duration)}`}
      // Tall enough to take a finger (44px) on a phone; the track itself
      // is drawn thin, centred in it.
      className='h-11 w-full min-w-0 cursor-pointer accent-primary sm:h-6'
    />
  );
  if (compact) {
    return (
      // On a phone the time goes under the bars: beside them it squeezed
      // the waveform into a few pixels.
      <div
        className={cn(
          'flex min-w-0 flex-col sm:flex-row sm:items-center sm:gap-3',
          className
        )}
      >
        {slider}
        <span className='shrink-0 font-mono text-xs tabular-nums text-muted-foreground'>
          {formatClock(currentTime)} / {formatClock(duration)}
        </span>
      </div>
    );
  }
  return (
    <div className={className}>
      {slider}
      <div className='mt-1 flex justify-between font-mono text-xs tabular-nums text-muted-foreground'>
        <span>{formatClock(currentTime)}</span>
        <span>{formatClock(duration)}</span>
      </div>
    </div>
  );
}

/** One chip that steps through the speeds rather than a menu. */
export function SpeedChip({
  rate,
  onChange,
}: {
  rate: number;
  onChange: (rate: number) => void;
}) {
  const next = SPEEDS[(SPEEDS.indexOf(rate) + 1) % SPEEDS.length] ?? 1;
  return (
    <button
      type='button'
      onClick={() => onChange(next)}
      aria-label={`Playback speed ${rate}x`}
      className='tap-target min-w-[3rem] shrink-0 rounded-md border border-foreground/10 px-2 py-1 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
    >
      {rate}x
    </button>
  );
}
